import { schemaResolver, useForm } from '@mantine/form'
import { useDebouncedCallback } from '@mantine/hooks'
import { modals } from '@mantine/modals'
import { replaceEqualDeep, useMutation } from '@tanstack/react-query'
import { useCallback, useEffect, useReducer, useRef } from 'react'
import { z } from 'zod'

import { useGlobalState } from '@/hooks/useGlobalState'
import type {
  CreateFAQsParams,
  GenerateDemographicResponse,
  GenerateMetadataResponse,
  GenerateObjectivesResponse,
  GetContentModuleResponse,
  GetFAQsResponse,
  GetStepsResponse,
  ScrapeFailureReason,
  UpdateContentModuleParams,
  UpdateFAQsParams,
  UpdateStepParams,
} from '@/services/api/api.types'

import { load, remove, save } from './storage'

/**
 *
 * Schemas
 *
 */
export const contentSchema = z.object({
  // contentId: z.nullable(z.string()), // not needed for now
  file: z.nullable(z.instanceof(File)),
  text: z.string().min(1, "The content can't be blank."), // content: for GET; text: for POST
})

/**
 * Split a module's files into the primary and the rest.
 *
 * The designated `primaryStepId` wins when there is one — decision 4, on C2-2596, which
 * made that field the portal's to set. Step order is the fallback for a module saved
 * before the primary could be chosen, and for the moment before the choice round-trips.
 */
export function splitMediaSteps<T extends { stepId?: string }>(
  steps: T[] | null | undefined,
  primaryStepId?: string | null,
): { primary?: T; supplementary: T[] } {
  if (!steps || steps.length === 0) return { supplementary: [] }

  const designatedIndex = primaryStepId ? steps.findIndex(step => step.stepId === primaryStepId) : -1
  const index = designatedIndex >= 0 ? designatedIndex : 0

  return {
    primary: steps[index],
    supplementary: steps.filter((_, i) => i !== index),
  }
}

const GENERIC_PROCESS_ERROR =
  'There was an issue processing this file. This may be due to the file being too large or in an unsupported format.'

// Placeholder wording, taken from sme_app's reference client. Courtney owns the final copy.
const SCRAPE_FAILURE_MESSAGES: Partial<Record<ScrapeFailureReason, string>> = {
  site_blocked:
    "This website doesn't allow its content to be read automatically, so it can't be added. Try a different URL, or add the content manually.",
  url_unreachable:
    "We couldn't reach this URL. Check that the address is spelled correctly and that the page exists, then try again.",
  timeout: 'The website took too long to respond. This is often temporary, so try adding the URL again in a moment.',
}

const SCRAPE_FAILURE_FALLBACK =
  "We couldn't read the content from this URL. Try a different URL, or add the content manually."

export function getScrapeFailureMessage(reason?: ScrapeFailureReason | null): string {
  return (reason && SCRAPE_FAILURE_MESSAGES[reason]) || SCRAPE_FAILURE_FALLBACK
}

// Maps terse backend processing errors to user-friendly copy.
export function getProcessErrorMessage(raw?: string | null, reason?: ScrapeFailureReason | null): string {
  // A failed file stays in the list until it is removed, unlike in Add File where it is discarded.
  if (reason) return `${getScrapeFailureMessage(reason)} Delete this file first to replace it.`
  if (!raw) return GENERIC_PROCESS_ERROR
  if (/failed to scrape url/i.test(raw)) {
    return "We couldn't read the content from this URL. The site may block automated access or be temporarily unavailable. Please delete this file and add a different URL or upload the content directly."
  }
  return GENERIC_PROCESS_ERROR
}

export const moduleObjectSchema = z
  .object({
    taskId: z.nullable(z.string()),
    name: z.string().min(1, 'Title is required.'),
    shortDescription: z.string().min(1, 'Introduction is required.'),
    presentationMode: z.enum(['sequential', 'member_directed']),
    access: z.enum(['restricted', 'open']),
    expirationDate: z.date().optional(),
    dueDate: z.date().optional(),
    categories: z.array(z.string()),
    mappings: z.array(z.string()).optional(),
    selectEsk: z.string().min(1, 'Sidekick selection is required.'),
    content: contentSchema,
    demographic: z.string(),
    priority: z.number().optional(),
    sectionObjectives: z.string().optional(),
    titleImage: z.nullable(z.string()),
    introduction: z.string(),
    personality: z.string().optional(),
    presentationObjectives: z.string().optional(),
    guidance: z.string(),
    deliveryStyle: z.string(),
    // The recorded choice behind the prose. Empty on a module saved before the picker
    // existed, which is how decision 16 tells a custom style from a picked one.
    deliveryStyleName: z.string(),
    visibleToMsk: z.boolean(),
    allowedForAllSponsors: z.boolean(),
  })
  .partial({ expirationDate: true, dueDate: true })

/**
 * Requiredness does not vary by channel or by delivery mode — decision 13. Courtney's
 * call: the media fields "weren't optional so much as invisible".
 */
export const moduleSchema = moduleObjectSchema.superRefine((data, ctx) => {
  if (!data.introduction || data.introduction.trim().length === 0) {
    ctx.addIssue({ code: 'custom', message: 'Introduction is required.', path: ['introduction'] })
  }

  if (!data.deliveryStyle || data.deliveryStyle.trim().length === 0) {
    ctx.addIssue({ code: 'custom', message: 'Delivery Mode is required.', path: ['deliveryStyle'] })
  }
})

/**
 * Required module-form fields. Shared between `ActionsSection` (submit) and
 * `ContentModuleFormScreen.sectionValidation` (per-section validity dots) so they
 * can't drift apart.
 */
export type ModuleFormFieldName = keyof z.infer<typeof moduleObjectSchema>

export function getRequiredModuleFields(): {
  topicSettings: ModuleFormFieldName[]
  requiredSettings: ModuleFormFieldName[]
  deliveryMode: ModuleFormFieldName[]
  presentation: ModuleFormFieldName[]
  all: ModuleFormFieldName[]
} {
  const topicSettings: ModuleFormFieldName[] = ['selectEsk']
  const presentation: ModuleFormFieldName[] = ['presentationMode']
  const requiredSettings: ModuleFormFieldName[] = ['name', 'shortDescription', 'introduction']
  const deliveryMode: ModuleFormFieldName[] = ['deliveryStyle']

  return {
    topicSettings,
    requiredSettings,
    deliveryMode,
    presentation,
    all: [...topicSettings, ...presentation, ...requiredSettings, ...deliveryMode],
  }
}

export const faqSchema = z.object({
  faqId: z.nullable(z.string()),
  question: z.string().min(1, 'Question is required.'),
  answer: z.string().min(1, "Answer can't be empty."),
})

export const stepSchema = z.object({
  stepId: z.nullable(z.string()),
  content: contentSchema,
  name: z.string().min(1, 'Title is required.'),
  titleImage: z.nullable(z.string()),
  sectionObjectives: z.string().min(1, 'Section objectives are required.'),
  introduction: z.nullable(z.string()), // esk introduction
  // deliveryMode: z.nativeEnum(DeliveryMode),
  // access: z.enum(['restricted', 'open']), // privacy setting - NOT IMPLEMENTED
})

/**
 *
 * Form Hooks
 *
 */
export function useContentModuleForm(opts?: { initialData?: GetContentModuleResponse }) {
  const shouldCache = useRef(false)

  const { api, uiStore } = useGlobalState()

  const cmChangeStatusMutation = useMutation({
    mutationFn: api.changeContentModuleStatus,
    onSuccess: (d, _vars) => {
      const success = 'status' in d && d.status
      const error = 'errors' in d

      if (success) {
        api.queryClient.invalidateQueries({
          queryKey: ['cm', 'list'],
        })
      } else if (error) {
        console.error('Error updating module status', d)
      }
    },
    onError: error => {
      console.error('Error updating module status', error)
    },
  })

  const debouncedSaveToCache = useDebouncedCallback(() => {
    saveToCache()
  }, 500)

  // Debounced counter for parent sectionValidation to recompute without per-keystroke renders.
  const [valuesVersion, bumpValuesVersion] = useReducer((c: number) => c + 1, 0)
  const debouncedBumpVersion = useDebouncedCallback(bumpValuesVersion, 600)

  /**
   * Whether the expert still has the restore prompt in front of them.
   *
   * A ref rather than state because the answer has to be right *within the commit that opens
   * the modal*: the restore effect and anything else keyed on `form.initialized` run in the
   * same pass, and a `setState` here would not be visible to them until the next one. The
   * counter beside it exists only to re-render once the question is answered.
   */
  const draftPendingRef = useRef(false)
  const [, decideDraft] = useReducer((c: number) => c + 1, 0)
  const isDraftPending = useCallback(() => draftPendingRef.current, [])

  /**
   * A draft was found and the expert has neither restored nor discarded it.
   *
   * Escape, the close button and a click outside all close the prompt without running
   * `onCancel`, so they answer nothing — and nothing else may treat that silence as a
   * decision. It is separate from `draftPendingRef` because the two release at different
   * times: the generation gate opens as soon as the prompt is gone, so the feature still
   * works, while the draft itself stays untouched until the expert says what to do with it.
   */
  const draftUndecidedRef = useRef(false)

  const resolveDraft = useCallback((decided: boolean) => {
    draftPendingRef.current = false
    if (decided) draftUndecidedRef.current = false
    decideDraft()
  }, [])

  const form = useForm<z.infer<ModuleFormSchema>>({
    mode: 'uncontrolled',
    validate: schemaResolver(moduleSchema, { sync: true }),
    validateInputOnBlur: true,
    initialValues: castContentModuleToZodForm(opts?.initialData),
    onValuesChange() {
      debouncedSaveToCache()
      debouncedBumpVersion()
    },
  })

  function loadFromCache() {
    const values = form.getValues()

    if (values.taskId) {
      return load(`@sidekick/forms/cm/${values.taskId}`)
    } else {
      return null
    }
  }

  function saveToCache() {
    const values = form.getValues()

    // An undecided draft is not ours to overwrite, and this is the other writer of the same
    // key. `markSaved` already refuses to clear it, but a pass's own auto-accepted values make
    // the form dirty and touched for the render before they are untouched again, which arms
    // the cache and writes the machine's text straight over the expert's unanswered draft.
    // Nothing is offered or lost in the meantime: they are asked again next visit, and the
    // leave guard still stops them walking away from anything they type now.
    if (draftUndecidedRef.current) return

    if (shouldCache.current && values.taskId) {
      save(`@sidekick/forms/cm/${values.taskId}`, values)
    }
  }

  function clearCache() {
    const taskId = form.getValues().taskId

    if (taskId) {
      remove(`@sidekick/forms/cm/${taskId}`)
    }
  }

  /**
   * The module has just been written to the server outside the Save button.
   *
   * Re-derives whether there is still anything worth caching, rather than clearing and
   * walking away. Clearing alone does not hold: `shouldCache` stays armed and the next value
   * change writes the draft straight back — the delivery-mode demotion is exactly such a
   * change, and the expert is then asked to restore a mode they never chose. But latching it
   * *off* is worse, because the effect that arms it is edge-triggered on dirty and touched:
   * if the expert has their own unsaved edit those stay true, the effect never runs again,
   * and caching would be dead for the rest of the session with their work unprotected.
   *
   * So: drop the stale draft, then ask the form what is genuinely still unsaved and write
   * that instead. Callers are expected to have already rebased the fields they saved.
   */
  function markSaved() {
    // An undecided draft is not stale work this save has superseded — it is the expert's, and
    // they have not been asked yet. Clearing it here deletes it with no confirmation, and
    // re-caching the current values over it does the same thing by another route. It keeps
    // until they answer the prompt, which they will get again next visit.
    if (draftUndecidedRef.current) return

    clearCache()
    shouldCache.current = form.isDirty() && form.isTouched()
    if (shouldCache.current) saveToCache()
  }

  // Only re-run the navigation blocker effect when dirty/touched actually changes.
  const prevDirtyTouchedRef = useRef({ dirty: false, touched: false })

  useEffect(() => {
    const isDirty = form.isDirty()
    const isTouched = form.isTouched()
    const prev = prevDirtyTouchedRef.current
    const isNewCM = uiStore.isNewContentModule

    if (prev.dirty === isDirty && prev.touched === isTouched && !isNewCM) {
      return
    }
    prevDirtyTouchedRef.current = { dirty: isDirty, touched: isTouched }

    const deleteNewCM = () => {
      if (isNewCM) {
        uiStore.setIsNewContentModule(false)
        // Delete the new content module from the database
        const taskId = form.getValues().taskId
        if (taskId) {
          cmChangeStatusMutation.mutate({
            task_id: taskId,
            status: 'removed',
          })
        }
      }
    }

    if (isDirty && isTouched) {
      shouldCache.current = true
      saveToCache()

      uiStore.setRouterLocationChangeFn(async () => {
        let resolveFn: ((v: boolean) => void) | undefined

        const confirmation = new Promise<boolean>(resolve => (resolveFn = resolve))

        modals.openConfirmModal({
          title: 'Unsaved Changes',
          centered: true,
          children: `You have unsaved changes. Are you sure you want to discard them?`,
          labels: { confirm: 'Discard Changes', cancel: 'Cancel' },
          confirmProps: { color: 'red' },
          groupProps: { mt: 'xl' },
          onConfirm: () => {
            clearCache()
            resolveFn?.(true)
            deleteNewCM()
          },
          onCancel: () => resolveFn?.(false),
        })

        const confirmed = await confirmation

        return confirmed
      })
    } else if (isNewCM) {
      uiStore.setRouterLocationChangeFn(async () => {
        let resolveFn: ((v: boolean) => void) | undefined

        const confirmation = new Promise<boolean>(resolve => (resolveFn = resolve))

        modals.openConfirmModal({
          title: 'Unsaved New Content Module',
          centered: true,
          children: `Leaving this page will discard your new content module. Are you sure you want to continue?`,
          labels: { confirm: 'Discard Content Module', cancel: 'Cancel' },
          confirmProps: { color: 'red' },
          groupProps: { mt: 'xl' },
          onConfirm: () => {
            clearCache()
            resolveFn?.(true)
            deleteNewCM()
          },
          onCancel: () => resolveFn?.(false),
        })

        const confirmed = await confirmation

        return confirmed
      })
    }

    return () => {
      uiStore.setRouterLocationChangeFn(undefined)
    }
  }, [form.isDirty(), form.isTouched()])

  useEffect(() => {
    if (form.initialized) {
      const cached = loadFromCache()

      // A draft identical to what just loaded is not unsaved work, and offering to restore it
      // asks the expert about a difference that does not exist. Reachable without anyone
      // typing since SME-1135: a generated pass writes the fields, which arms the cache, and
      // the pass then saves them — so the draft and the server agree by the time this runs.
      //
      if (cached && draftMatchesValues(cached, form.getValues())) {
        clearCache()
        return
      }

      if (cached) {
        // Marked before the modal opens, not after it is answered. Anything else keyed on
        // `form.initialized` runs in this same commit — the primary-file pass among them — and
        // a pass that starts here would be reading the *server's* blanks while the expert's
        // own text sits in the draft waiting to be restored. It would then generate over the
        // restored value and save the result, and the usual defence does not apply: typing
        // cancels a running generation, but `setValues` raises no input event.
        draftPendingRef.current = true
        draftUndecidedRef.current = true
        modals.openConfirmModal({
          title: 'Restore Unsaved Changes',
          centered: true,
          children: `There are unsaved changes from your previous session. Would you like to restore them?`,
          labels: { confirm: 'Restore Changes', cancel: 'Cancel' },
          groupProps: { mt: 'xl' },
          // Every route out of the modal has to release the pass, including escape and a
          // click outside, or a dismissed prompt blocks generation for the rest of the visit.
          onClose: () => resolveDraft(false),
          onConfirm: () => {
            form.setValues(cached)
            form.setTouched({ taskId: true })
            resolveDraft(true)
          },
          onCancel: () => {
            clearCache()
            resolveDraft(true)
          },
        })
      }
    }
  }, [form.initialized])

  return { form, schema: moduleSchema, contentSchema, valuesVersion, markSaved, isDraftPending }
}

export function useFAQForm() {
  const form = useForm<z.infer<FAQFormSchema>>({
    mode: 'controlled',
    validate: schemaResolver(faqSchema, { sync: true }),
    validateInputOnBlur: true,
    initialValues: castFAQToZodForm(),
  })

  return { form, schema: faqSchema }
}

export function useStepForm() {
  const form = useForm<z.infer<StepFormSchema>>({
    mode: 'controlled',
    validate: schemaResolver(stepSchema, { sync: true }),
    validateInputOnBlur: true,
    initialValues: castStepToZodForm(),
  })

  return { form, schema: stepSchema }
}

export function useMetadataForm() {
  return useForm<MetadataFormSchema>({
    mode: 'controlled',
  })
}

/**
 *
 * Data Transformation
 *
 */
/**
 * Whether a stored draft says the same thing as the form's current values.
 *
 * Both sides go through the cache's own round trip first, and that is the whole point.
 * `replaceEqualDeep` returns its first argument only when the two have the same number of
 * keys, and `JSON.stringify` drops any whose value is `undefined` — `priority` is one,
 * since the schema has it optional and `castContentModuleToZodForm` writes `?? undefined`.
 * Comparing a stored draft against live values directly therefore counts one key fewer on
 * the stored side and never matches, however identical the two are in substance.
 *
 * `false` on anything unserialisable, which reads as "cannot say they match" — the safe
 * answer, since it only means the expert is asked.
 */
export function draftMatchesValues(cached: unknown, values: unknown): boolean {
  try {
    const current = JSON.parse(JSON.stringify(values))
    return replaceEqualDeep(cached, current) === cached
  } catch {
    return false
  }
}

export function castContentModuleToZodForm(data?: Partial<GetContentModuleResponse>): z.infer<ModuleFormSchema> {
  return {
    taskId: data?.taskId ?? null,
    name: data?.name ?? '',
    shortDescription: data?.shortDescription ?? '',
    presentationMode: data?.areStepSequential ? 'sequential' : 'member_directed',
    selectEsk: data?.selectEsk ?? 'PSK',
    categories: data?.categories ?? [],
    mappings: data?.mappings ?? [],
    content: { text: '', file: null },
    demographic: data?.demographic ?? '',
    priority: data?.priority ?? undefined,
    visibleToMsk: false,
    allowedForAllSponsors: data?.allowedForAllSponsors ?? false,
    sectionObjectives: data?.sectionObjectives ?? '',
    titleImage: data?.titleImage ?? null,
    introduction: data?.introduction ?? '',
    personality: data?.presentationPersonality ?? '',
    presentationObjectives: data?.presentationObjectives ?? '',
    guidance: data?.guidance ?? '',
    deliveryStyle: data?.presentationStyle ?? '',
    deliveryStyleName: data?.presentationStyleName ?? '',
    // mock
    access: 'open',
  }
}

export function castFAQToZodForm(data?: Partial<GetFAQsResponse['0']>): z.infer<FAQFormSchema> {
  return {
    faqId: data?.faqId ?? null,
    question: data?.question ?? '',
    answer: data?.answer ?? '',
  }
}

export function castZodFormToContentModule(
  data: z.infer<ModuleFormSchema>,
): Omit<UpdateContentModuleParams, 'task_id'> {
  // C2-2598 derives `is_media_mode` from the recorded choice, so that is all the portal
  // sends. A module with no choice — decision 16's legacy Custom case — sends neither, and
  // the backend leaves the stored flag exactly as it is (`prepare_for_db` drops nulls
  // before the $set). The stored value is the one the member app reads, so preserving it
  // beats any guess the portal could make from the prose.
  const deliveryMode = data.deliveryStyleName?.trim() ? { presentation_style_name: data.deliveryStyleName.trim() } : {}

  return {
    name: data.name,
    title: data.name,
    short_description: data.shortDescription,
    are_step_sequential: data.presentationMode === undefined ? undefined : data.presentationMode === 'sequential',
    select_esk: data.selectEsk,
    categories: data.categories,
    mappings: data.mappings,
    long_description: 'NOT_IMPLEMENTED_FRONT_END',
    demographic: data.demographic,
    visible_to_msk: false,
    allowed_for_all_sponsors: data.allowedForAllSponsors,
    title_image: data.titleImage,
    introduction: data.introduction,
    section_objectives: data.sectionObjectives || null, // Include section objectives when saving
    presentation_personality: data.personality,
    presentation_objectives: data.presentationObjectives,
    presentation_style: data.deliveryStyle,
    ...deliveryMode,
    guidance: data.guidance,
  }
}

export function castZodFormToFAQsUpdate(data: z.infer<FAQFormSchema>): Omit<UpdateFAQsParams, 'task_id' | 'step_id'> {
  return {
    faqs: [
      {
        question: data.question,
        answer: data.answer,
        faq_id: data.faqId!,
      },
    ],
  }
}

export function castZodFormToFAQsCreate(data: z.infer<FAQFormSchema>): Omit<CreateFAQsParams, 'task_id' | 'step_id'> {
  return {
    faqs: [
      {
        question: data.question,
        answer: data.answer,
      },
    ],
  }
}

export function castStepToZodForm(data?: Partial<GetStepsResponse[0]>): z.infer<StepFormSchema> {
  return {
    stepId: data?.stepId ?? null,
    content: { text: '', file: null },
    name: data?.name ?? '',
    titleImage: data?.titleImage ?? '',
    introduction: data?.introduction ?? '',
    // deliveryMode: data?.deliveryMode ?? stepSchema.shape.deliveryMode.enum.Paraphrase,
    sectionObjectives: data?.sectionObjectives ?? '',
    // mock
    // access: 'open',
  }
}

export function castZodFormToStep(data: z.infer<StepFormSchema>): Omit<UpdateStepParams, 'step_id'> {
  return {
    name: data.name,
    title_image: data.titleImage ?? '',
    introduction: data.introduction ?? '',
    section_objectives: data.sectionObjectives,
    // delivery_mode: data.deliveryMode,
  }
}

export type ModuleFormSchema = typeof moduleSchema
export type FAQFormSchema = typeof faqSchema
export type ContentFormSchema = typeof contentSchema
export type StepFormSchema = typeof stepSchema
export type MetadataFormSchema = {
  module?: GenerateMetadataResponse['generated'][number]
  step?: GenerateMetadataResponse['generated'][number]
  demographic?: GenerateDemographicResponse['demographic']
  sectionObjectives?: GenerateObjectivesResponse['objectives'] // deprecated
}
